import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {environment} from "@app/environment";
import {catchError, map, Observable, of, tap, throwError} from "rxjs";
import {User} from "../../core/_datamodel/user";
import {plainToInstance} from "class-transformer";
import {KBRequest} from "./datamodel";
import {Attachment} from "../../core/attachments/datamodel";
import {AttachmentsService} from "../../core/attachments/attachments.service";
import {MessageService} from "primeng/api";

@Injectable({
  providedIn: 'root',
})
export class KnowledgeBaseRequestsService {
  options = {
    // withCredentials: true,
    headers: new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('api-key', environment.API_KEY)
      .set('ngsw-bypass','true')
  };

  constructor(private http: HttpClient,
              private attachmentsService: AttachmentsService,
              private messageService: MessageService) {
    if(!environment.production) { console.debug('Knowledge Base Requests Service instanced!'); }
  }

  getKBRequests(user: User): Observable<KBRequest[]> {
    return this.http.post(environment.API_URL + '/knowledge-base-requests/getKBRequests',{user}, this.options).pipe(
        tap((data: any) => {
          if (!environment.production) console.debug('getKBRequests loaded:', data);
        }),
        map((data: any) => {
          // @ts-ignore
          return plainToInstance(KBRequest, data);
        }),
        catchError(err => {
          if (err.status === 401) {
            console.warn(err.error.message);
            return of(err);
          } else {
            return this.handleError(err);
          }
        })
      );
  }

  getKBRequest(kb_requests_id: number, user: User): Observable<KBRequest> {
    return this.http.post(environment.API_URL + '/knowledge-base-requests/getKBRequest',{kb_requests_id, user}, this.options).pipe(
      tap((data: any) => {
        if (!environment.production) console.debug('getKBRequest loaded:', data);
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(KBRequest, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  createKBRequest(request: KBRequest): Observable<KBRequest> {
    return this.http.post(environment.API_URL + '/knowledge-base-requests/createKBRequest', request, this.options).pipe(
      tap(() => {
        if (!environment.production) {
          console.debug('createKBRequest success');
        }
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(KBRequest, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  saveKBRequest(request: KBRequest): Observable<KBRequest> {
    return this.http.post(environment.API_URL + '/knowledge-base-requests/saveKBRequest', request, this.options).pipe(
      tap(() => {
        if (!environment.production) {
          console.debug('saveKBRequest success');
        }
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(KBRequest, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  updateKBRequest(request: KBRequest): Observable<KBRequest> {
    return this.http.post(environment.API_URL + '/knowledge-base-requests/updateKBRequest', request, this.options).pipe(
      tap(() => {
        if (!environment.production) {
          console.debug('updateKBRequest success');
        }
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(KBRequest, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  acceptKBRequest(request: KBRequest): Observable<KBRequest> {
    return this.http.post<KBRequest & { attachment?: Attachment }>(environment.API_URL + '/knowledge-base-requests/acceptKBRequest', request, this.options).pipe(
      tap(data => {
        if (!environment.production) {
          console.debug('acceptKBRequest success');
        }
        if (data.attachment && this.attachmentsService.shouldAutoIndex(data.attachment)) {
          const warnIndexingFailed = () => this.messageService.add({
            severity: 'warn',
            summary: 'Indexing could not start',
            detail: 'KB request accepted. Indexing could not start; use "Index document" on the repository attachment to retry.',
            sticky: true
          });
          // Keep indexing independent of the approval subscription and its success state.
          this.attachmentsService.indexAttachment(data.attachment).subscribe({
            next: result => {
              if (result !== true) warnIndexingFailed();
            },
            error: warnIndexingFailed
          });
        }
      }),
      map(({ attachment, ...record }) => plainToInstance(KBRequest, record)),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  rejectKBRequest(request: KBRequest): Observable<KBRequest> {
    return this.http.post(environment.API_URL + '/knowledge-base-requests/rejectKBRequest', request, this.options).pipe(
      tap(() => {
        if (!environment.production) {
          console.debug('rejectKBRequest success');
        }
      }),
      map((data: any) => {
        // @ts-ignore
        return plainToInstance(KBRequest, data);
      }),
      catchError(err => {
        if (err.status === 401) {
          console.warn(err.error.message);
          return of(err);
        } else {
          return this.handleError(err);
        }
      })
    );
  }

  private handleError(err: HttpErrorResponse) {
    let errorMessage;
    if (err.error instanceof Error) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      errorMessage = err.error.message;
    }
    console.error(errorMessage);
    return throwError(() => {
      return new Error(errorMessage)
    });
  }

}
